/**
 * Expenses view — read-only table with working receipt viewing + upload.
 *
 * Edit / Delete buttons remain stubbed until chunk 5. The receipt buttons
 * however work end-to-end: View fetches a signed URL from the receipts
 * Storage bucket; Upload picks a file, compresses (if image), uploads, and
 * updates the row through DB.updateByUuid so the cloud row gets the new path.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { createRowSelection } from "../components/rowSelection";
import { Lists } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar, todayISO } from "../utils/format";
import { render } from "../state";
import { processReceiptFile } from "../lib/receipt";
import { uploadReceiptDataUrl, deleteReceiptByPath, isStoragePath } from "../lib/storage";
import { viewReceipt } from "../components/receiptViewer";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import type { Row } from "../lib/types";

const sel = createRowSelection("expenses");
function expensesEdit(uuid: string): void { (window as any).editExpense?.(uuid); }
function expensesDelete(uuid: string): void { (window as any).deleteExpense?.(uuid); }

function findExpenseByUuid(uuid: string): Row | null {
  if (!uuid) return null;
  return DB.table("expenses").find(r => r && r._uuid === uuid) || null;
}

export function viewExpenses(root: HTMLElement): void {
  const exp = DB.table("expenses").slice().sort((a, b) =>
    String(b["Date"] || "").localeCompare(String(a["Date"] || ""))
  );
  const total = sum(exp, r => r["Amount"]);
  const deductible = sum(
    exp.filter(r => String(r["Deductible"] || "").toLowerCase().startsWith("y")),
    r => r["Amount"],
  );

  const exp_total = exp.length;
  const exp_page = slicePage("expenses", exp, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Expenses",    value: fmtN(exp.length) },
      { label: "Total",       value: fmt$0(total) },
      { label: "Deductible",  value: fmt$0(deductible) },
      { label: "Avg Expense", value: fmt$0(exp.length ? total / exp.length : 0) },
    ])}
    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: expensesEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: expensesDelete },
      ])}
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Date</th><th>Category</th><th>Vendor</th><th class="text-right">Amount</th>
          <th>Deductible</th><th>Method</th><th>File</th><th>Ref/Link</th><th>Notes</th>
        </tr></thead>
        <tbody>
          ${exp_page.map(r => {
            const u = escapeHtml(r._uuid || "");
            return `<tr ${sel.rowAttrs(u, `editExpense('${u}')`)}>
              <td>${escapeHtml(fmtDate(r["Date"]))}</td>
              <td><span class="pill pill-amber">${escapeHtml(r["Category"] || "")}</span></td>
              <td>${escapeHtml(r["Vendor"] || "")}</td>
              <td class="text-right stat-num">${fmt$(r["Amount"])}</td>
              <td>${escapeHtml(r["Deductible"] || "")}</td>
              <td>${escapeHtml(r["Payment Method"] || "")}</td>
              <td class="whitespace-nowrap">${
                r["Receipt File"]
                  ? `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="viewReceiptForExpense('${u}')" title="${escapeHtml(r['Receipt File Name'] || 'View receipt')}">${icon("paperclip")} View</button><button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="uploadReceiptForExpense('${u}')" title="Replace receipt">${icon("download")}</button>`
                  : `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="uploadReceiptForExpense('${u}')" title="Upload receipt">${icon("paperclip")} Upload</button>`
              }</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Receipt Link/Ref"] || "")}</td>
              <td class="text-xs" title="${escapeHtml(r["Notes"] || "")}">${escapeHtml(r["Notes"] || "")}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="9" class="text-center text-[var(--muted)] py-8">No expenses.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("expenses", exp_total, 50)}
  `;
  sel.applyUI();
}

/* ============================================================
   Receipt actions wired up directly for chunk 4c
   ============================================================ */
export async function viewReceiptForExpense(uuid: string): Promise<void> {
  const row = findExpenseByUuid(uuid);
  if (!row || !row["Receipt File"]) return;
  await viewReceipt(row["Receipt File"], row["Receipt File Type"], row["Receipt File Name"]);
}

export async function uploadReceiptForExpense(uuid: string): Promise<void> {
  const row = findExpenseByUuid(uuid);
  if (!row) { toast("Could not locate expense.", "err"); return; }
  const inp = document.createElement("input");
  inp.type = "file";
  inp.accept = "image/*,application/pdf";
  inp.onchange = async () => {
    const file = inp.files && inp.files[0];
    if (!file) return;
    try {
      const result = await processReceiptFile(file);
      toast("Uploading…");
      const path = await uploadReceiptDataUrl(result.dataUrl, result.name, result.type);
      const old = row["Receipt File"];
      if (isStoragePath(old)) { deleteReceiptByPath(old).catch(() => {}); }
      const merged = { ...row, "Receipt File": path, "Receipt File Name": result.name, "Receipt File Type": result.type };
      await DB.updateByUuid("expenses", row._uuid!, merged);
      toast("Receipt uploaded");
      render();
    } catch (err: any) {
      toast("Upload failed: " + (err?.message || err), "err");
    }
  };
  inp.click();
}

// Expose inline-handler bridges
(window as any).viewReceiptForExpense   = viewReceiptForExpense;
(window as any).uploadReceiptForExpense = uploadReceiptForExpense;

/* ============================================================
   Expense form (editExpense / saveExpense / deleteExpense) + in-form receipt UI
   ============================================================ */
function renderExpenseReceiptUI(row: Partial<Row>): string {
  const has = !!row["Receipt File"];
  const name = row["Receipt File Name"] || "";
  return `
    <button type="button" class="btn btn-ghost" onclick="document.getElementById('expenseReceiptInput').click()">${icon("paperclip")} ${has ? 'Replace receipt' : 'Upload receipt'}</button>
    ${has ? `
      <button type="button" class="btn btn-ghost" onclick="viewExpenseReceiptFromForm()">${icon("eye")} View</button>
      <button type="button" class="btn btn-ghost" onclick="clearExpenseReceipt()">${icon("x")} Remove</button>
      <span class="text-xs text-[var(--muted)] truncate" style="max-width:240px">${escapeHtml(name || 'attached')}</span>
    ` : `<span class="text-xs text-[var(--muted)]">No receipt attached</span>`}`;
}

export function editExpense(uuid?: string): void {
  const isNew = !uuid;
  const row = isNew
    ? { "Date": todayISO(), "Deductible": "Yes" }
    : findExpenseByUuid(uuid!);
  if (!row) { toast("Expense not found", "err"); return; }

  const body = `
    <form id="expenseForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(row["Date"]), required: true })}
      ${formField({ label: "Category", name: "Category", value: row["Category"], options: Lists.get("Expense Category"), required: true })}
      ${formField({ label: "Vendor", name: "Vendor", value: row["Vendor"] })}
      ${formField({ label: "Amount ($)", name: "Amount", type: "number", value: row["Amount"], required: true })}
      ${formField({ label: "Deductible", name: "Deductible", value: row["Deductible"], options: ["Yes", "No", "Partial"] })}
      ${formField({ label: "Payment Method", name: "Payment Method", value: row["Payment Method"], options: ["Credit", "Debit", "Cash", "ACH", "PayPal", "Other"] })}
      ${formField({ label: "Receipt Link / Ref #", name: "Receipt Link/Ref", value: row["Receipt Link/Ref"] })}
      <div class="sm:col-span-2">
        <label class="label">Receipt file (image or PDF)</label>
        <input type="hidden" name="Receipt File"      value="${escapeHtml(row["Receipt File"] || "")}">
        <input type="hidden" name="Receipt File Name" value="${escapeHtml(row["Receipt File Name"] || "")}">
        <input type="hidden" name="Receipt File Type" value="${escapeHtml(row["Receipt File Type"] || "")}">
        <input type="file" id="expenseReceiptInput" accept="image/*,application/pdf" class="hidden" onchange="onExpenseReceiptUpload(event)">
        <div id="expenseReceiptUI" class="flex flex-wrap gap-2 items-center">${renderExpenseReceiptUI(row)}</div>
        <div class="text-xs text-[var(--muted)] mt-2">Images are auto-compressed before upload. Files are stored privately in your Supabase Storage bucket.</div>
      </div>
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
    </form>`;
  const u = isNew ? "" : escapeHtml(uuid!);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteExpense('${u}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveExpense('${u}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Expense" : "Edit Expense", body, footer });
}

async function onExpenseReceiptUpload(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    const result = await processReceiptFile(file);
    const form = document.getElementById("expenseForm") as HTMLFormElement | null;
    if (!form) return;
    (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value = result.dataUrl;
    (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value = result.name;
    (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value = result.type;
    document.getElementById("expenseReceiptUI")!.innerHTML = renderExpenseReceiptUI({
      "Receipt File": result.dataUrl,
      "Receipt File Name": result.name,
      "Receipt File Type": result.type,
    });
    markModalDirty();
    toast("Receipt attached — click Save to commit");
  } catch (err: any) {
    toast("Upload failed: " + (err?.message || err), "err");
  }
}

function clearExpenseReceipt(): void {
  const form = document.getElementById("expenseForm") as HTMLFormElement | null;
  if (!form) return;
  (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value = "";
  (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value = "";
  (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value = "";
  document.getElementById("expenseReceiptUI")!.innerHTML = renderExpenseReceiptUI({});
  markModalDirty();
}

async function viewExpenseReceiptFromForm(): Promise<void> {
  const form = document.getElementById("expenseForm") as HTMLFormElement | null;
  if (!form) return;
  const fileRef = (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value;
  const type    = (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value;
  const name    = (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value;
  await viewReceipt(fileRef, type, name);
}

export async function saveExpense(uuid: string): Promise<void> {
  const d = readForm($("#expenseForm"));
  const isNew = !uuid;
  // Double-deduction guard: grading fees are already CAPITALIZED into card
  // basis by Grading submissions (they flow to COGS on sale). Logging the
  // same invoice here too would deduct it twice.
  if (String(d["Category"] || "") === "Grading") {
    if (!confirm("Heads up: grading fees logged through Grading submissions are already capitalized into the cards' cost basis and will hit the books as COGS when those cards sell.\n\nLogging the same invoice as an Expense too would DOUBLE-DEDUCT it. Only continue if this expense is NOT tied to a submission.\n\nSave this expense anyway?")) return;
  }
  // If a freshly-attached receipt is still a data URL, push it to Storage now.
  const fileRef = d["Receipt File"];
  if (fileRef && typeof fileRef === "string" && fileRef.startsWith("data:")) {
    try {
      toast("Uploading receipt…");
      d["Receipt File"] = await uploadReceiptDataUrl(fileRef, d["Receipt File Name"], d["Receipt File Type"]);
    } catch (e: any) {
      toast("Receipt upload failed: " + (e?.message || e), "err");
      return;
    }
  }
  // Editing: if the user replaced or cleared a previously-stored receipt, evict the old file.
  if (!isNew) {
    const prev = findExpenseByUuid(uuid);
    const oldRef = prev?.["Receipt File"];
    if (isStoragePath(oldRef) && oldRef !== d["Receipt File"]) {
      deleteReceiptByPath(oldRef as string).catch(() => {});
    }
  }
  try {
    if (isNew) await DB.add("expenses", d);
    else       await DB.updateByUuid("expenses", uuid, d);
  } catch (e: any) {
    console.error("[saveExpense]", e);
    toast("Cloud sync failed: " + (e?.message || e), "err");
    return;
  }
  toast("Expense saved");
  closeModal();
  render();
}

export async function deleteExpense(uuid: string): Promise<void> {
  if (!uuid) return;
  if (!confirm("Delete this expense?")) return;
  const row = findExpenseByUuid(uuid);
  if (row && isStoragePath(row["Receipt File"])) {
    deleteReceiptByPath(row["Receipt File"]).catch(() => {});
  }
  try { await DB.removeByUuid("expenses", uuid); }
  catch (e: any) {
    console.error("[deleteExpense]", e);
    toast("Cloud delete failed: " + (e?.message || e), "err");
    return;
  }
  toast("Deleted");
  closeModal();
  render();
}

// Inline-handler bridges
(window as any).editExpense                = editExpense;
(window as any).saveExpense                = saveExpense;
(window as any).deleteExpense              = deleteExpense;
(window as any).onExpenseReceiptUpload     = onExpenseReceiptUpload;
(window as any).clearExpenseReceipt        = clearExpenseReceipt;
(window as any).viewExpenseReceiptFromForm = viewExpenseReceiptFromForm;
